"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useRouter, usePathname } from "next/navigation";
import { ChatItem } from "./sidebar-history-item";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { isDocumentType } from "@/lib/dropDownData";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import { setAddNewHistoryItem } from "@/lib/history-updater";

function groupChatsByDate(history) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const toValidDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return null;
      const d = /^\d+$/.test(s) ? new Date(Number(s)) : new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const getItemDate = (item) => {
    const candidates = [
      item?.updatedAt,
      item?.updated_at,
      item?.lastMessageAt,
      item?.last_message_at,
      item?.createdAt,
      item?.created_at,
      item?.timestamp,
    ];
    for (const c of candidates) {
      const d = toValidDate(c);
      if (d) return d;
    }
    return new Date();
  };

  const startOfDayMs = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const labelForDate = (d) => {
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return formatDate(d);
  };

  const sorted = [...(history || [])].sort(
    (a, b) => getItemDate(b).getTime() - getItemDate(a).getTime()
  );

  const groupsByDay = new Map();
  for (const item of sorted) {
    const d = getItemDate(item);
    const dayKey = startOfDayMs(d);
    if (!groupsByDay.has(dayKey)) {
      groupsByDay.set(dayKey, { key: dayKey, label: labelForDate(d), chats: [] });
    }
    groupsByDay.get(dayKey).chats.push(item);
  }

  return Array.from(groupsByDay.values()).sort((a, b) => b.key - a.key);
}

export function SidebarHistory({ forcedTab, className, showEmptyState = true } = {}) {
  const { setOpenMobile } = useSidebar();
  const router = useRouter();
  const pathname = usePathname();
  const { id, screen_id } = useParams();
  const { activeScreenId, activeTab } = useSelector((state) => state.main)

  const [renamingId, setRenamingId] = useState(null);
  // const [userDetail, setUserDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData, isCreateModeifyWebsite } = useSelector(
    (state) => state.main
  );
  const userId = userData._id;
  const isProjectPage = pathname.startsWith("/project");
  const resolvedTab = forcedTab ?? activeTab;
  const effectiveTab = isProjectPage ? resolvedTab : resolvedTab ?? "Page Builder";
  const chatType = useMemo(() => {
  if (effectiveTab === "Card Maker") return "card_maker";
  if (effectiveTab === "Page Builder") return "page_builder";
  if (effectiveTab === "Go-to-Market Strategy") return "go_to_market_strategy";
  if (effectiveTab) return effectiveTab.toLowerCase().replace(/[\s\/]+/g, "_");
  return "chat";
}, [effectiveTab]);

  const fetchHistory = async (chatType) => {
    try {
      if (!userData?._id) {
        console.warn("User ID not available yet");
        return;
      }
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/all/${userId}?chat_type=${chatType}&t=${Date.now()}`,
        {
          credentials: "include",
          cache: "no-store",
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      const data = await res.json();
      if (data.status === 1 && Array.isArray(data.details)) {
        // Apply localStorage backup for any missing titles
        const renameBackup = JSON.parse(localStorage.getItem('chatRenames') || '{}');
        const currentScreenIds = new Set(data.details.map(item => String(item.screen_id)));

        // Clean up localStorage entries for deleted chats
        const cleanedBackup = Object.fromEntries(
          Object.entries(renameBackup).filter(([screenId]) => currentScreenIds.has(String(screenId)))
        );
        if (Object.keys(cleanedBackup).length !== Object.keys(renameBackup).length) {
          localStorage.setItem('chatRenames', JSON.stringify(cleanedBackup));
        }

        const updatedDetails = data.details.map(item => {
          if (cleanedBackup[item.screen_id] && (!item.title || item.title.trim() === '')) {
            // console.log(`Applying localStorage backup for ${item.screen_id}:`, cleanedBackup[item.screen_id]);
            return { ...item, title: cleanedBackup[item.screen_id] };
          }
          return item;
        });

        // Persist redesign flags so the badge survives follow-up edits
        const redesignFlagsRaw = localStorage.getItem("redesignFlags") || "{}";
        const redesignFlags = JSON.parse(redesignFlagsRaw);
        const cleanedRedesignFlags = Object.fromEntries(
          Object.entries(redesignFlags).filter(([screenId]) => currentScreenIds.has(String(screenId)))
        );
        let redesignChanged = Object.keys(cleanedRedesignFlags).length !== Object.keys(redesignFlags).length;
        updatedDetails.forEach((item) => {
          const looksLikeRedesign =
            Boolean(item?.redesignPrompt) ||
            (item?.prompt && item.prompt.toLowerCase().startsWith("redesign the website at"));
          const screenIdKey = String(item.screen_id);
          if (looksLikeRedesign && !cleanedRedesignFlags[screenIdKey]) {
            cleanedRedesignFlags[screenIdKey] = true;
            redesignChanged = true;
          }
        });
        if (redesignChanged) {
          localStorage.setItem("redesignFlags", JSON.stringify(cleanedRedesignFlags));
        }

        setHistory(updatedDetails);
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error("Failed to fetch history", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  if (!userId) return;
  fetchHistory(chatType);
}, [userId, chatType, isCreateModeifyWebsite]);

  useEffect(() => {
    if (!loading) {
      setAddNewHistoryItem((item) => {
        setHistory((prev) => [item, ...prev]);
      });
    }
  }, [loading]);

 
  useEffect(() => {
    const handleChatRenamed = (event) => {
      const { screenId, newTitle } = event.detail;
      setHistory((prev) =>
        prev.map((h) =>
          h.screen_id === screenId ? { ...h, title: newTitle } : h
        )
      );
    };

    window.addEventListener("chatRenamed", handleChatRenamed);
    return () => window.removeEventListener("chatRenamed", handleChatRenamed);
  }, []);


  if (loading) {
    return (
      <SidebarGroup className={className}>
        <SidebarGroupContent>
          <div>Loading chat history...</div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (chatType !== "chat" && history.length === 0) {
    if (!showEmptyState) return null;
    return (
      <SidebarGroup className={className}>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            Your conversations will appear here once you start chatting!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }


  const groupedHistory = groupChatsByDate(history);


  return (
    <>
      <SidebarGroup className={className}>
        <SidebarGroupContent>
          <SidebarMenu>
            {groupedHistory.map(({ key, label, chats }) => (
              <div key={key} className="mb-1">
                <div className="px-2 py-1 text-xs text-sidebar-foreground/50">
                  {label}
                </div>
                {chats.map((item) => (
                  <ChatItem
                    key={item.screen_id}
                    item={item}
                    isActive={item.screen_id === screen_id}
                    onDelete={(chatId) => {
                      setHistory((prev) =>
                        prev.filter((h) => h.screen_id !== chatId)
                      );
                      if (chatId === screen_id) {
                        router.replace("/");
                      } else if (chatId === activeScreenId) {
                        window.location.href = "/";
                      }
                    }}
                    setOpenMobile={setOpenMobile}
                    setHistory={setHistory}
                    renamingId={renamingId}
                    setRenamingId={setRenamingId}
                  />
                ))}
              </div>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}
